import DOMPurify from 'dompurify';
import { z } from 'zod';

/**
 * OWASP TOP 10 Security Best Practices Implementation
 */

// A1:2021 - Broken Access Control
// Implement proper role-based access control
export const checkAccess = (userRole: string, requiredRole: string): boolean => {
    const roleHierarchy: Record<string, number> = {
        guest: 0,
        user: 1,
        editor: 2,
        admin: 3,
        superadmin: 4,
    };
    return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0);
};

// A2:2021 - Cryptographic Failures
// Use secure storage for sensitive data
export const secureStorage = {
    setItem: (key: string, value: string): void => {
        // In production, use encryption before storing
        sessionStorage.setItem(key, value);
    },
    getItem: (key: string): string | null => {
        return sessionStorage.getItem(key);
    },
    removeItem: (key: string): void => {
        sessionStorage.removeItem(key);
    },
};

// A3:2021 - Injection
// Sanitize all user inputs
export const sanitizeInput = (input: string): string => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
};

export const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html);
};

// A4:2021 - Insecure Design
// Validate all data with schemas
export const consignmentSchema = z.object({
    id: z.string().min(1).max(50),
    shipment: z.string().min(1).max(100),
    vehicle: z.string().min(1).max(20),
    arrivingTo: z.string().min(1).max(200),
    consignmentNumber: z.string().regex(/^[A-Z]{3}-\d{4}$/),
    dispatched: z.string(),
    waybill: z.string().regex(/^WB-\d{6}$/),
    sender: z.string().min(1).max(100),
});

export const searchQuerySchema = z.string().max(200).transform(sanitizeInput);

// A5:2021 - Security Misconfiguration
// Security headers (to be set in server/CDN)
export const securityHeaders = {
    'Content-Security-Policy': "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline'",
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
};

// A6:2021 - Vulnerable and Outdated Components
// Keep dependencies updated (handled by package management)

// A7:2021 - Identification and Authentication Failures
// Rate limiting for login attempts
const loginAttempts = new Map<string, { count: number; lastAttempt: number }>();

export const checkRateLimit = (identifier: string, maxAttempts = 5, windowMs = 900000): boolean => {
    const now = Date.now();
    const attempts = loginAttempts.get(identifier);

    if (!attempts || now - attempts.lastAttempt > windowMs) {
        loginAttempts.set(identifier, { count: 1, lastAttempt: now });
        return true;
    }

    if (attempts.count >= maxAttempts) {
        return false;
    }

    attempts.count++;
    attempts.lastAttempt = now;
    return true;
};

// A8:2021 - Software and Data Integrity Failures
// Verify data integrity
export const generateChecksum = async (data: string): Promise<string> => {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

// A9:2021 - Security Logging and Monitoring Failures
// Implement security logging
export const securityLog = (event: string, details: Record<string, unknown>): void => {
    const logEntry = {
        timestamp: new Date().toISOString(),
        event,
        details,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };

    // In production, send to logging service
    console.log('[SECURITY]', JSON.stringify(logEntry));
};

// A10:2021 - Server-Side Request Forgery (SSRF)
// Validate URLs before making requests
const allowedDomains = ['api.example.com', 'localhost'];

export const isValidUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url);
        return allowedDomains.some(domain => parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`));
    } catch {
        return false;
    }
};

// CSRF Token generation
export const generateCSRFToken = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

// XSS Prevention - escape special characters
export const escapeHtml = (str: string): string => {
    const htmlEscapes: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;',
    };
    return str.replace(/[&<>"'/]/g, (match) => htmlEscapes[match]);
};
